import { User } from 'crono-fe-common/types/user';
import {
  formatMissingVariableBrackets,
  getProspectName,
} from 'crono-fe-common/utils';
import { LanguageOption } from 'crono-fe-common/types/languageOption';
const idSeparator = '-id:';
import React from 'react';

export const dispartPascalCase = (str: string) => {
  return str.replace(/([a-z])([A-Z])/g, '$1 $2');
};

export const prospectTitle = (title?: string | null) => {
  const toRet = title && title != null && title !== 'null' ? title : null;
  if (toRet) {
    return `${toRet}`;
  }

  return '';
};

export const capitalizeFirstLetter = (string?: string) =>
  string ? string.charAt(0).toUpperCase() + string.slice(1) : '';

function deepQuerySelectorAll(
  selector: string,
  root: HTMLElement | ShadowRoot,
): HTMLParagraphElement[] {
  const results: HTMLParagraphElement[] = Array.from(
    root.querySelectorAll(selector),
  );
  const pushNestedResults = function (root: ShadowRoot) {
    deepQuerySelectorAll(selector, root).forEach((elem) => {
      if (!results.includes(elem)) {
        results.push(elem);
      }
    });
  };
  if (root instanceof HTMLElement && root.shadowRoot) {
    pushNestedResults(root.shadowRoot);
  }
  for (const elem of root.querySelectorAll('*')) {
    if (elem.shadowRoot) {
      pushNestedResults(elem.shadowRoot);
    }
  }
  return results;
}

export const formatHtmlContent = (content: string): string => {
  const formattedContent: string = formatMissingVariableBrackets(content);

  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = formattedContent ?? '';

  const pElements = deepQuerySelectorAll('p', tempDiv);
  for (let i = 0; i < pElements.length; i++) {
    const pElement = pElements[i];

    const currentStyle = pElement.getAttribute('style');

    let newStyle = currentStyle;
    if (!newStyle?.includes('margin')) {
      newStyle = currentStyle ? currentStyle + ' margin: 0px;' : 'margin: 0px;';
    }
    pElement.setAttribute('style', newStyle);
  }

  const lists = tempDiv.querySelectorAll('ul, ol');

  lists.forEach((item) => {
    if (!(item instanceof HTMLElement)) return;
    item.style.margin = '0';
    item.style.paddingLeft = '1.5em';
  });

  // The browser DOM always encodes & as &amp; when serialising innerHTML.
  // Email clients often don't decode &amp; in href attributes, breaking links
  // that contain query-string parameters. Decode it back to & in hrefs only.
  const modifiedHtmlString = tempDiv.innerHTML.replace(
    /\bhref="([^"]*)"/gi,
    (_, url) => `href="${url.replace(/&amp;/g, '&')}"`,
  );
  return modifiedHtmlString;
};

export const trimEmptyPTags = (html: string) => {
  const regexStart = /^(\s*<p(?:\s+[^>]*)?>\s*<br\s*\/?>\s*<\/p>\s*)+/i;
  const regexEnd = /(\s*<p(?:\s+[^>]*)?>\s*<br\s*\/?>\s*<\/p>\s*)+$/i;

  let trimmedHtml = html.replace(regexStart, '');
  trimmedHtml = trimmedHtml.replace(regexEnd, '');

  return trimmedHtml;
};

export const trimAndCleanHtml = (html: string) => {
  const formattedHtml = formatHtmlContent(html);
  const trimmedHtml = trimEmptyPTags(formattedHtml);

  const sectionTagStartRegex = /^(\s*<section\b[^>]*>)/i;

  if (sectionTagStartRegex.test(trimmedHtml)) {
    const cleanedHtml = trimmedHtml.replace(
      /<section\b([^>]*)\bcontenteditable\s*=\s*["'](?:false|true)["']([^>]*)>/i,
      '<section$1$2>',
    );

    return cleanedHtml.replace('<section >', '<section>');
  } else {
    const emptyStyleRegex = /\sstyle=(["'])\s*\1/g;

    return html.replace(emptyStyleRegex, '');
  }
};

export const removeBackgroundColors = (content: string) => {
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = content;

  function removeBackgroundColorsNested(element: HTMLElement) {
    if (element.nodeType !== Node.ELEMENT_NODE) return;
    element.style.backgroundColor = '';
    for (const child of element.children) {
      if (!(child instanceof HTMLElement)) return;
      removeBackgroundColorsNested(child);
    }
  }
  removeBackgroundColorsNested(tempDiv);
  return tempDiv.innerHTML;
};

export const removeTags = (
  htmlString: string,
  tagsToRemove: string[],
): string => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlString, 'text/html');

  tagsToRemove.forEach((tag) => {
    const elements = doc.querySelectorAll(tag);
    elements.forEach((element) => {
      const parent = element.parentNode;
      if (!parent) return;
      while (element.firstChild) {
        parent.insertBefore(element.firstChild, element);
      }
      parent.removeChild(element);
    });
  });

  return doc.body.innerHTML;
};

export const formatLinkedinUrl = (url: string, maxLength: number) => {
  if (url.length <= maxLength) return url;

  const truncated = `...${url.slice(-(maxLength - 3))}`;

  return truncated;
};

export const truncateChartLabel = (label: string) =>
  label.length > 18 ? `${label.slice(0, 18)}..` : label;

export const removeIdFromChartLabel = (label: string) => {
  const regex = new RegExp(`${idSeparator}.*$`);
  return label.replace(regex, '');
};

export const formatAxisLabel = (value: string) => {
  const cleaned = removeIdFromChartLabel(value);
  return truncateChartLabel(cleaned);
};

export const formatToPercents = (value: string) => `${value}%`;

export const formatSecondsToString = (totalSeconds: number) => {
  if (totalSeconds < 60) {
    return `${totalSeconds} sec`;
  }

  if (totalSeconds < 3600) {
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;

    return (
      `${minutes > 0 ? `${minutes} min` : ''}` +
      `${seconds > 0 ? ` ${seconds} sec` : ''}`
    ).trim();
  }

  const hours = Math.floor(totalSeconds / 3600);
  const remainder = totalSeconds % 3600;
  const minutes = Math.floor(remainder / 60);

  return (
    `${hours} hour${hours > 1 ? 's' : ''}` +
    (minutes > 0 ? ` ${minutes} min` : '')
  );
};

export function formatAmount(
  amount: number | null | undefined,
  currency?: string | null,
): string {
  const symbol = currency ?? '';
  if (amount == null || isNaN(amount)) return `${symbol}0`;

  const sign = amount < 0 ? '-' : '';
  const value = Math.abs(amount);

  const UNITS = [
    { threshold: 1e12, divider: 1e12, suffix: 'T' },
    { threshold: 1e9, divider: 1e9, suffix: 'B' },
    { threshold: 1e6, divider: 1e6, suffix: 'M' },
    { threshold: 1e4, divider: 1e3, suffix: 'K' },
  ];

  for (const { threshold, divider, suffix } of UNITS) {
    if (value >= threshold) {
      const truncated = Math.trunc((value / divider) * 100) / 100;
      const formatted = new Intl.NumberFormat('de-DE', {
        maximumFractionDigits: 2,
      }).format(truncated);
      return `${sign}${symbol}${formatted}${suffix}`;
    }
  }

  if (value >= 1_000) {
    // European thousands notation: 1.000, 2.500, etc. — floor to avoid rounding up
    const formatted = new Intl.NumberFormat('de-DE', {
      maximumFractionDigits: 0,
    }).format(Math.floor(value));
    return `${sign}${symbol}${formatted}`;
  }

  const formatted = new Intl.NumberFormat('en-US', {
    maximumFractionDigits: 1,
  }).format(value);
  return `${sign}${symbol}${formatted}`;
}

export const removeTrailingEmptyParagraphs = (html: string) => {
  return html.replace(/(?:<p><br><\/p>)+$/, '');
};

export const formatInSemicolonString = (
  toBeMerged: (string | React.JSX.Element)[],
  label?: string,
) => {
  if (!label) {
    return toBeMerged.join('; ');
  }
  return `${label} ${toBeMerged.join('; ')}`;
};

export const hexToRgba = (hex: string, alpha: number) => {
  const r = parseInt(hex.substring(1, 3), 16);
  const g = parseInt(hex.substring(3, 5), 16);
  const b = parseInt(hex.substring(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

export const stringAvatarUser = (user: User | null) => {
  if (!user) return '';

  return (user.firstName?.at(0) ?? '') + (user.lastName?.at(0) ?? '');
};

export const stringAvatarProspect = (
  prospect: {
    name?: string | null;
    email?: string | null;
    firstName?: string | null;
    lastName?: string | null;
  } | null,
) => {
  if (!prospect) return '';

  const text = getProspectName(prospect);

  return text
    .toUpperCase()
    .split(' ')
    .map((word) => word[0])
    .join('')
    .substring(0, 2);
};

export const mapLanguageToLabel = (language: LanguageOption) => {
  switch (language) {
    case 'en':
      return 'English';
    case 'es':
      return 'Spanish';
    case 'fr':
      return 'French';
    case 'de':
      return 'German';
    case 'it':
      return 'Italian';
    case 'pt':
      return 'Portuguese';
    case 'nl':
      return 'Dutch';
    case 'no':
      return 'Norwegian';
    case 'sv':
      return 'Swedish';
    case 'da':
      return 'Danish';
    case 'fi':
      return 'Finnish';
    case 'pl':
      return 'Polish';
    case 'ro':
      return 'Romanian';
    case 'bg':
      return 'Bulgarian';
    case 'hu':
      return 'Hungarian';
    case 'el':
      return 'Greek';
    case 'lt':
      return 'Lithuanian';
    case 'tr':
      return 'Turkish';
    case 'all':
      return 'All languages';
    default:
      return 'English';
  }
};

export const formatCallDuration = (seconds?: number | null): string => {
  if (!seconds) return '0s';

  const total = Math.max(0, Math.floor(seconds));

  const MINUTE = 60;
  const HOUR = 3600;
  const DAY = 86400;
  const MONTH = DAY * 30;
  const YEAR = DAY * 365;

  if (total < MINUTE) {
    return `${total}s`;
  }

  if (total < HOUR) {
    const m = Math.floor(total / MINUTE);
    const s = total % MINUTE;
    return s === 0 ? `${m}m` : `${m}m ${s}s`;
  }

  if (total < DAY) {
    const h = Math.floor(total / HOUR);
    const m = Math.floor((total % HOUR) / MINUTE);
    return m === 0 ? `${h}h` : `${h}h ${m}m`;
  }

  if (total < MONTH) {
    const d = Math.floor(total / DAY);
    const h = Math.floor((total % DAY) / HOUR);
    return h === 0 ? `${d}d` : `${d}d ${h}h`;
  }

  if (total < YEAR) {
    const mo = Math.floor(total / MONTH);
    const d = Math.floor((total % MONTH) / DAY);
    return d === 0 ? `${mo}mo` : `${mo}mo ${d}d`;
  }

  const y = Math.floor(total / YEAR);
  const mo = Math.floor((total % YEAR) / MONTH);
  return mo === 0 ? `${y}y` : `${y}y ${mo}mo`;
};

/**
 * Formats a millisecond duration into a compact human label, e.g.
 * `5s`, `1m 20s`, `9m 42s`, `1h 2m 3s`. Returns `-` for missing/invalid values.
 * Minutes/hours are omitted when zero; seconds are shown when they remain or
 * when no larger unit is present.
 *
 * Sub-second durations are rendered down to their first significant decimal so
 * they never collapse to `0s`, e.g. `237ms` → `0.2s`, `20ms` → `0.02s`.
 */
export function formatDurationMs(ms: number | null | undefined): string {
  if (ms == null || Number.isNaN(ms) || ms < 0) {
    return '-';
  }

  if (ms > 0 && ms < 1000) {
    const seconds = ms / 1000;
    const decimals = Math.floor(-Math.log10(seconds)) + 1;
    return `${Number(seconds.toFixed(decimals))}s`;
  }

  const totalSeconds = Math.round(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  const parts: string[] = [];
  if (hours > 0) {
    parts.push(`${hours}h`);
  }
  if (minutes > 0) {
    parts.push(`${minutes}m`);
  }
  if (seconds > 0 || parts.length === 0) {
    parts.push(`${seconds}s`);
  }

  return parts.join(' ');
}

interface FormatDurationNumericalOptions {
  showHours?: boolean;
  padding?: {
    hours?: number;
    minutes?: number;
    seconds?: number;
  };
}

/**
 * Formats a duration in seconds into a numerical clock string.
 * `mm:ss` by default, or `hh:mm:ss` when `showHours` is true.
 * Returns `''` for missing values. Each segment is zero-padded to the
 * corresponding `padding` width (default 2).
 */
export function formatDurationNumerical(
  seconds?: number | null,
  options?: FormatDurationNumericalOptions,
): string {
  if (seconds == null) {
    return '';
  }

  const {
    showHours = false,
    padding: {
      hours: hoursPadding = 2,
      minutes: minutesPadding = 2,
      seconds: secondsPadding = 2,
    } = {},
  } = options ?? {};

  const total = Math.max(0, Math.floor(seconds));

  const totalMinutes = showHours
    ? Math.floor((total % 3600) / 60)
    : Math.floor(total / 60);

  const h = Math.floor(total / 3600)
    .toString()
    .padStart(hoursPadding, '0');
  const m = totalMinutes.toString().padStart(minutesPadding, '0');
  const s = (total % 60).toString().padStart(secondsPadding, '0');

  return showHours ? `${h}:${m}:${s}` : `${m}:${s}`;
}
